import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert as MuiAlert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { gql, useQuery, useMutation } from '@apollo/client'
import { ContentHead } from '../../../../components/common/ContentHead'
import { EmptyData } from '../../../../components/common/EmptyData'
import { Alert } from '../../../../components/common/Alert'
import { CardBox } from '../../../../theme/styleComponents'
import { format, isValid } from 'date-fns'

const safeFormat = (val: unknown, fmt: string): string => {
  if (!val) return '-'
  const d = new Date(val as string | number)
  return isValid(d) ? format(d, fmt) : '-'
}

const GET_BILL_EMAIL_BATCHES = gql`
  query GetBillEmailBatches {
    getBillEmailBatches {
      batches {
        id
        billingCycle
        periodYear
        periodMonth
        status
        createdAt
        updatedAt
        items {
          id
          status
        }
      }
    }
  }
`

const TRIGGER_BATCH = gql`
  mutation TriggerBillEmailBatch($input: TriggerBatchInput!) {
    triggerBillEmailBatch(input: $input) {
      batch {
        id
        status
      }
    }
  }
`

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const statusColor = (status: string): 'default' | 'warning' | 'info' | 'success' | 'error' => {
  switch (status) {
    case 'PENDING': return 'warning'
    case 'SENDING': return 'info'
    case 'COMPLETED': return 'success'
    case 'PARTIAL': return 'error'
    default: return 'default'
  }
}

const BatchEmails: FC = () => {
  const navigate = useNavigate()

  const { data, loading, refetch } = useQuery(GET_BILL_EMAIL_BATCHES, {
    fetchPolicy: 'network-only'
  })

  const [triggerBatch, { loading: triggering }] = useMutation(TRIGGER_BATCH, {
    onCompleted: () => {
      Alert.success('Batch email job triggered successfully')
      refetch()
    },
    onError: (err) => Alert.error(err.message)
  })

  const batches = data?.getBillEmailBatches?.batches ?? []

  const handleTrigger = async (batchId: number) => {
    await triggerBatch({ variables: { input: { batchId } } })
  }

  return (
    <CardBox marginTop={3}>
      <ContentHead title="Batch Email Queue" />

      <MuiAlert severity="info" sx={{ mb: 2 }}>
        Emails will only be sent to clients for bills in <strong>Final Bill</strong>, <strong>Sent To Client</strong>,{' '}
        <strong>Partially Paid</strong> or <strong>Paid</strong> status. Bills still in approval will be processed on
        subsequent sends, and bills already emailed from the bill page are not emailed again.
      </MuiAlert>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : batches.length === 0 ? (
        <Box display="flex" justifyContent="center" py={6}>
          <EmptyData title="No email batches found" />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Billing Cycle</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.map((batch: any) => {
                const pendingCount = (batch.items ?? []).filter((i: any) => i.status === 'PENDING').length
                return (
                  <TableRow key={batch.id} hover sx={{ cursor: 'pointer' }}>
                    <TableCell onClick={() => navigate(`/accounts/batch-emails/${batch.id}`)}>
                      <Typography variant="body2" fontWeight={500}>#{batch.id}</Typography>
                    </TableCell>
                    <TableCell onClick={() => navigate(`/accounts/batch-emails/${batch.id}`)}>
                      {batch.billingCycle}
                    </TableCell>
                    <TableCell onClick={() => navigate(`/accounts/batch-emails/${batch.id}`)}>
                      {MONTH_NAMES[(batch.periodMonth ?? 1) - 1]} {batch.periodYear}
                    </TableCell>
                    <TableCell onClick={() => navigate(`/accounts/batch-emails/${batch.id}`)}>
                      <Chip label={batch.status} color={statusColor(batch.status)} size="small" />
                    </TableCell>
                    <TableCell onClick={() => navigate(`/accounts/batch-emails/${batch.id}`)}>
                      {safeFormat(batch.createdAt, 'MMM d, yyyy h:mm a')}
                    </TableCell>
                    <TableCell align="right">
                      {/* <Button
                        size="small"
                        variant="contained"
                        disabled={pendingCount === 0 || triggering}
                        onClick={() => handleTrigger(batch.id)}
                      >
                        {pendingCount > 0 ? `Send (${pendingCount})` : 'Send'}
                      </Button> */}
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1 }}
                        onClick={() => navigate(`/accounts/batch-emails/${batch.id}`)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </CardBox>
  )
}

export default BatchEmails
